import { useRef, useEffect, useLayoutEffect, useState } from "react";
import { ArrowUpRight, ScanLine, MessageCircle, Gauge } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { gsap, usePrefersReducedMotion } from "@/hooks/useSmoothScroll";
import { NAP_BEAT_ASSETS, PROJECTS } from "@/lib/siteConfig";

const NAP = PROJECTS.find((p) => p.id === "naperformance");
const RED = "#dc2626"; // dezentes Racing-Rot — CloudService bleibt dominant

const useIsDesktop = () => {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const fn = () => setD(window.innerWidth >= 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return d;
};

// Lazy autoplay video: loads + plays only when visible, pauses offscreen.
const LazyVideo = ({ src, webm, poster, width, height, className = "", testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (v.preload === "none") v.preload = "auto";
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      data-testid={testId}
      poster={poster}
      width={width}
      height={height}
      muted
      loop
      playsInline
      preload="none"
      className={`block w-full h-auto ${className}`}
    >
      <source src={src} type="video/mp4" />
      {webm && <source src={webm} type="video/webm" />}
    </video>
  );
};

const Kicker = ({ children }) => (
  <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: RED }} aria-hidden="true" />
    {children}
  </p>
);

// img with intrinsic dimensions — prevents lazy-load layout shift
const Shot = ({ asset, eager = false, className = "" }) => (
  <img
    src={asset.src}
    alt={asset.alt}
    width={asset.w}
    height={asset.h}
    loading={eager ? "eager" : "lazy"}
    className={`block w-full h-auto ${className}`}
  />
);

/* ============ Beats 2–4 — gefuehrter Advisor-Flow (Desktop pinned, Phone-zentriert) ============ */
/* WICHTIG (Stabilität): Stacked- und Pinned-Variante sind EIGENE Komponenten.
   GSAP `pin: true` re-parented die Section in einen pin-spacer. Würde dieselbe
   Komponente nur ihr JSX tauschen, entfernt React den Host-Node VOR dem
   Effekt-Cleanup -> removeChild-Crash (schwarze Seite). Als separate Komponente
   laufen die Layout-Effekt-Cleanups (ctx.revert) garantiert VOR der DOM-Entfernung. */
const AdvisorFlowStacked = () => {
  const { t } = useLang();
  const steps = NAP_BEAT_ASSETS.advisorFlowSteps;
  return (
    <div className="px-4 sm:px-6" data-testid="nap-flow-stacked">
      <div className="max-w-[1200px] mx-auto">
        <Reveal>
          <Kicker>{t.nap.stepKicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nap.stepTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.nap.stepText}</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-6 max-w-md">
          <Reveal delay={0.08}>
            <DeviceFramePhone testId="nap-stacked-upload">
              <Shot asset={steps[0]} />
            </DeviceFramePhone>
          </Reveal>
          <Reveal delay={0.16}>
            <DeviceFramePhone testId="nap-stacked-analysis">
              <Shot asset={steps[2]} />
            </DeviceFramePhone>
          </Reveal>
        </div>
        <ol className="mt-8 space-y-4" data-testid="nap-steps-stacked-list">
          {t.nap.steps.map((s) => (
            <li key={s.num} className="flex items-start gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full border border-white/15 flex items-center justify-center text-[10px] font-mono-cs text-foreground/70">
                {s.num}
              </span>
              <div>
                <p className="text-sm font-medium text-foreground">{s.label}</p>
                <p className="mt-0.5 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

const AdvisorFlowPinned = () => {
  const { t } = useLang();
  const sec = useRef(null);
  const imgRefs = useRef([]);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const steps = NAP_BEAT_ASSETS.advisorFlowSteps;

  // useLayoutEffect: Cleanup (ctx.revert -> Unpin) läuft synchron BEVOR React
  // den gepinnten Node aus dem DOM entfernt — verhindert removeChild-Crashes.
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const imgs = imgRefs.current.filter(Boolean);
      gsap.set(imgs.slice(1), { autoAlpha: 0 });
      gsap.set(imgs[0], { autoAlpha: 1 });
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: sec.current,
          start: "top top",
          end: "+=220%",
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          onUpdate: (self) => {
            const idx = Math.min(2, Math.floor(self.progress * 3));
            if (idx !== activeRef.current) {
              activeRef.current = idx;
              setActive(idx);
            }
          },
        },
      });
      tl.to({}, { duration: 3 });
      imgs.forEach((img, i) => {
        if (i === 0) return;
        tl.to(imgs[i - 1], { autoAlpha: 0, scale: 1.015, duration: 0.3, ease: "power1.inOut" }, i - 0.15);
        tl.fromTo(img, { autoAlpha: 0, scale: 0.99 }, { autoAlpha: 1, scale: 1, duration: 0.3, ease: "power1.inOut" }, i - 0.15);
      });
    }, sec);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={sec} data-testid="nap-flow-pinned" className="relative h-screen flex items-center overflow-hidden">
      <div className="max-w-[1280px] w-full mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 items-center">
        <div className="col-span-6 xl:col-span-5">
          <Kicker>{t.nap.stepKicker}</Kicker>
          <h2 className="mt-4 font-display text-2xl xl:text-3xl font-semibold tracking-[-0.02em] text-foreground leading-tight">
            {t.nap.stepTitle}
          </h2>
          <p className="mt-3 text-sm xl:text-base text-muted-foreground leading-relaxed">{t.nap.stepText}</p>
          {/* Stepper */}
          <ol className="mt-9 space-y-5" data-testid="nap-stepper-list">
            {t.nap.steps.map((s, i) => (
              <li key={s.num} className="flex items-start gap-4">
                <span
                  className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center text-[11px] font-mono-cs transition-[background-color,border-color,color,box-shadow] duration-300 ${
                    active === i
                      ? "text-white border-transparent"
                      : active > i
                      ? "text-foreground/70 border-white/20"
                      : "text-muted-foreground/50 border-white/10"
                  }`}
                  style={active === i ? { backgroundColor: RED, boxShadow: "0 0 16px rgba(220,38,38,0.45)" } : undefined}
                >
                  {s.num}
                </span>
                <div className={`transition-opacity duration-300 ${active === i ? "opacity-100" : "opacity-45"}`}>
                  <p className="text-base font-medium text-foreground">{s.label}</p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed max-w-sm">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="col-span-6 xl:col-span-7 flex justify-center">
          <div className="relative w-[270px] xl:w-[300px]">
            <div
              aria-hidden="true"
              className="absolute -inset-14 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(220,38,38,0.10), transparent 70%)" }}
            />
            <DeviceFramePhone testId="nap-flow-phone">
              <div className="relative" style={{ aspectRatio: "880/1633" }}>
                {steps.map((s, i) => (
                  <img
                    key={s.src}
                    ref={(el) => (imgRefs.current[i] = el)}
                    src={s.src}
                    alt={s.alt}
                    width={s.w}
                    height={s.h}
                    loading={i === 0 ? "eager" : "lazy"}
                    className="absolute inset-0 w-full h-full object-cover will-change-transform"
                    data-testid={`nap-flow-screen-${i}`}
                  />
                ))}
              </div>
            </DeviceFramePhone>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdvisorFlow = () => {
  const reduced = usePrefersReducedMotion();
  const isDesktop = useIsDesktop();
  return !isDesktop || reduced ? <AdvisorFlowStacked /> : <AdvisorFlowPinned />;
};

/* ============ Hauptsektion ============ */
export const NAPerformanceCaseStudy = () => {
  const { t } = useLang();

  return (
    <section
      id="case-naperformance"
      data-testid="case-study-naperformance"
      className="relative py-20 sm:py-28 bg-[#070a12] border-y border-white/5 overflow-hidden"
    >
      {/* dezenter roter Glow — Projektfarbe respektiert, CloudService dominant */}
      <div
        aria-hidden="true"
        className="absolute top-[1%] left-[-12%] w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(220,38,38,0.07), transparent 70%)" }}
      />

      {/* ---- Header ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Kicker>{t.nap.kicker}</Kicker>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.08] text-foreground max-w-3xl">
            {t.nap.headline}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl">{t.nap.intro}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {NAP.allBadges.map((b) => (
              <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
                {b}
              </Badge>
            ))}
          </div>
          <a
            data-testid="nap-visit-link"
            href={NAP.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
          >
            {t.nap.visit} <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>
      </div>

      {/* ---- Beat 1 — Premium Performance Experience ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-16" data-testid="nap-beat-1">
        <Reveal>
          <Kicker>{t.nap.b1Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nap.b1Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.nap.b1Text}</p>
        </Reveal>
        <Reveal delay={0.12} className="mt-9">
          <div className="relative">
            <DeviceFrameDesktop url="naperformance.at" testId="nap-hero-frame">
              <Shot asset={NAP_BEAT_ASSETS.heroDesktop} eager />
            </DeviceFrameDesktop>
            <div className="absolute -bottom-10 -right-2 sm:right-8 w-[24%] max-w-[170px]">
              <DeviceFramePhone testId="nap-hero-phone">
                <Shot asset={NAP_BEAT_ASSETS.heroMobile} />
              </DeviceFramePhone>
            </div>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 2 — OCR Kerntechnologie ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nap-beat-2">
        <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
              <ScanLine className="w-3.5 h-3.5" style={{ color: RED }} />
              {t.nap.ocrKicker}
            </p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-[-0.02em] text-foreground">{t.nap.ocrTitle}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.nap.ocrText}</p>
          </div>
          <div className="lg:col-span-8 order-1 lg:order-2">
            <DeviceFrameDesktop url="naperformance.at" testId="nap-ocr-frame">
              <Shot asset={NAP_BEAT_ASSETS.ocrDesktop} />
            </DeviceFrameDesktop>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <DeviceFrameDesktop url="naperformance.at" testId="nap-upload-frame">
              <Shot asset={NAP_BEAT_ASSETS.uploadDesktop} />
            </DeviceFrameDesktop>
          </div>
          <p className="lg:col-span-4 text-sm text-muted-foreground leading-relaxed font-mono-cs">{t.nap.steps[0].text}</p>
        </Reveal>
      </div>

      {/* ---- Beats 2–4 — gefuehrter Advisor-Flow (pinned) ---- */}
      <div className="mt-24" data-testid="nap-beat-flow">
        <AdvisorFlow />
      </div>

      {/* ---- Beat 4 — Leistungsvergleich ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-20" data-testid="nap-beat-4">
        <Reveal>
          <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
            <Gauge className="w-3.5 h-3.5" style={{ color: RED }} />
            {t.nap.b4Kicker}
          </p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nap.b4Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.nap.b4Text}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="naperformance.at" testId="nap-analysis-frame">
              <Shot asset={NAP_BEAT_ASSETS.analysisDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="nap-compare-phone">
                <Shot asset={NAP_BEAT_ASSETS.compareMobile} />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 5 — Stage-Empfehlungen & AI Advisor ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nap-beat-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <Kicker>{t.nap.b5Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nap.b5Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.nap.b5Text}</p>
            <p className="mt-5 text-xs font-mono-cs text-muted-foreground/60 leading-relaxed" data-testid="nap-disclaimer">
              {t.nap.b5Note}
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="flex justify-center gap-6 sm:gap-10">
              <div className="w-[200px] sm:w-[240px] mt-10">
                <DeviceFramePhone testId="nap-stages-phone">
                  <Shot asset={NAP_BEAT_ASSETS.stagesMobile} />
                </DeviceFramePhone>
              </div>
              <div className="w-[200px] sm:w-[240px]">
                <DeviceFramePhone testId="nap-advisor-phone">
                  <Shot asset={NAP_BEAT_ASSETS.advisorMobile} />
                </DeviceFramePhone>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 6 — 24/7 KI-Chatberater ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nap-beat-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.12} className="lg:col-span-7 order-2 lg:order-1 flex justify-center">
            <div className="relative w-[250px] sm:w-[290px]">
              <div
                aria-hidden="true"
                className="absolute -inset-12 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(220,38,38,0.10), transparent 70%)" }}
              />
              <DeviceFramePhone testId="nap-chat-phone">
                <Shot asset={NAP_BEAT_ASSETS.chatMobile} />
              </DeviceFramePhone>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5 order-1 lg:order-2">
            <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
              <MessageCircle className="w-3.5 h-3.5" style={{ color: RED }} />
              {t.nap.b6Kicker}
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nap.b6Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.nap.b6Text}</p>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 7 — WhatsApp & Lead Flow + Live Flow ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-28" data-testid="nap-beat-7">
        <Reveal>
          <Kicker>{t.nap.b7Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nap.b7Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.nap.b7Text}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="naperformance.at" testId="nap-flow-desktop-frame">
              <LazyVideo
                src={NAP_BEAT_ASSETS.flowDesktop.src}
                webm={NAP_BEAT_ASSETS.flowDesktop.webm}
                poster={NAP_BEAT_ASSETS.flowDesktop.poster}
                width={NAP_BEAT_ASSETS.flowDesktop.w}
                height={NAP_BEAT_ASSETS.flowDesktop.h}
                testId="nap-flow-desktop-video"
              />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="nap-flow-phone-frame">
                <LazyVideo
                  src={NAP_BEAT_ASSETS.flowMobile.src}
                  webm={NAP_BEAT_ASSETS.flowMobile.webm}
                  poster={NAP_BEAT_ASSETS.flowMobile.poster}
                  width={NAP_BEAT_ASSETS.flowMobile.w}
                  height={NAP_BEAT_ASSETS.flowMobile.h}
                  testId="nap-flow-mobile-video"
                />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
